import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Collapse, IconButton } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { getCategoryColor, getCategoryLabel } from "../aiMappingColors";
import { groupDetectionsByCategory } from "../aiMappingDetections";
import { AiMappingDetectionLike } from "../aiMappingTypes";
import { AiMappingController } from "../useAiMappingController";
import { CollapsibleSection } from "./aiMappingResultsShared";

interface AiMappingDetectionsSectionProps {
  controller: AiMappingController;
  areaId: string;
  groups: ReturnType<typeof groupDetectionsByCategory>;
  total: number;
  sectionsOpen: boolean;
  onToggleSection: () => void;
  expandedGroups: Record<string, boolean>;
  onToggleGroup: (category: string) => void;
  quickAddActive: boolean;
  onOpenEdit: (event: React.MouseEvent<HTMLElement>, detection: AiMappingDetectionLike) => void;
}

export default function AiMappingDetectionsSection({
  controller,
  areaId,
  groups,
  total,
  sectionsOpen,
  onToggleSection,
  expandedGroups,
  onToggleGroup,
  quickAddActive,
  onOpenEdit,
}: AiMappingDetectionsSectionProps) {
  return (
    <CollapsibleSection
      title="Counts"
      subtitle={`${total} total counted items`}
      open={sectionsOpen}
      onToggle={onToggleSection}
      actions={
        <IconButton
          size="small"
          title={controller.detectionsVisible ? "Hide all counts" : "Show all counts"}
          onClick={() => controller.setDetectionsVisible(!controller.detectionsVisible)}
        >
          {controller.detectionsVisible ? (
            <VisibilityIcon fontSize="small" />
          ) : (
            <VisibilityOffIcon fontSize="small" />
          )}
        </IconButton>
      }
    >
      {groups.length === 0 && (
        <MDTypography variant="body2" color="text" textAlign="center" mt={1}>
          No counts found in this area. Try running detection again.
        </MDTypography>
      )}

      <MDBox display="flex" flexDirection="column" gap={1}>
        {groups.map((group) => {
          const groupVisible =
            controller.detectionsVisible && controller.isDetectionGroupVisible(group.category);
          return (
            <MDBox
              key={group.category}
              p={1}
              sx={{ border: "1px solid #e7ebf0", borderRadius: "14px", bgcolor: "#fff" }}
              data-testid={`ai-mapping-group-${group.category}`}
            >
              <MDBox
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ cursor: "pointer" }}
                onClick={() => onToggleGroup(group.category)}
              >
                <MDBox
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: group.color,
                    flexShrink: 0,
                  }}
                />
                <MDBox sx={{ flex: 1, minWidth: 0 }}>
                  <MDTypography variant="button" fontWeight="medium" sx={{ lineHeight: 1.1 }}>
                    {getCategoryLabel(group.category)}
                  </MDTypography>
                </MDBox>
                <IconButton
                  size="small"
                  disabled={controller.busy || !!controller.activeTask}
                  title={
                    quickAddActive && controller.quickAddCategory === group.category
                      ? "Stop adding detections"
                      : `Quick add: click the map to place ${getCategoryLabel(group.category)}`
                  }
                  sx={{
                    width: 28,
                    height: 28,
                    color:
                      quickAddActive && controller.quickAddCategory === group.category
                        ? "#1e88e5"
                        : "inherit",
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    controller.toggleQuickAdd(areaId, group.category);
                  }}
                  data-testid={`ai-mapping-quick-add-${group.category}`}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  title={
                    groupVisible
                      ? `Hide ${getCategoryLabel(group.category)}`
                      : `Show ${getCategoryLabel(group.category)}`
                  }
                  sx={{ width: 28, height: 28 }}
                  onClick={(event) => {
                    event.stopPropagation();
                    controller.toggleDetectionGroupVisible(group.category);
                  }}
                >
                  {groupVisible ? (
                    <VisibilityIcon fontSize="small" />
                  ) : (
                    <VisibilityOffIcon fontSize="small" />
                  )}
                </IconButton>
                <MDBox
                  sx={{
                    width: 32,
                    textAlign: "right",
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <MDTypography variant="caption" fontWeight={700} lineHeight={1}>
                    {group.count}
                  </MDTypography>
                </MDBox>
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    transform: expandedGroups[group.category] ? "rotate(180deg)" : "none",
                    transition: "transform 150ms ease",
                  }}
                />
              </MDBox>

              {(group.category === "single_stalls" || group.category === "double_stalls") && (
                <MDTypography variant="caption" color="text" display="block" ml={2.75} mt={0.25}>
                  {`${(
                    group.count * (group.category === "single_stalls" ? 20 : 25)
                  ).toLocaleString()} linear ft`}
                </MDTypography>
              )}

              <Collapse in={!!expandedGroups[group.category]}>
                <MDBox pt={1} display="flex" flexDirection="column" gap={0.5}>
                  {group.items.map((detection) => (
                    <MDBox
                      key={detection.id}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      px={0.75}
                      py={0.5}
                      sx={{
                        cursor: "pointer",
                        borderRadius: "8px",
                        bgcolor:
                          controller.selectedDetectionId === detection.id
                            ? "rgba(25,118,210,0.08)"
                            : "#f8fafc",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                      }}
                      onClick={() => controller.selectDetection(detection.id)}
                      data-testid={`ai-mapping-detection-row-${detection.id}`}
                    >
                      <MDBox
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: getCategoryColor(detection.category, detection.color),
                          flexShrink: 0,
                        }}
                      />
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{ flex: 1, lineHeight: 1.15 }}
                      >
                        {getCategoryLabel(detection.category)}
                        {typeof detection.confidence === "number"
                          ? ` · ${Math.round(detection.confidence * 100)}%`
                          : ""}
                      </MDTypography>
                      <IconButton
                        size="small"
                        disabled={controller.busy}
                        sx={{ width: 28, height: 28 }}
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenEdit(event, detection);
                        }}
                        data-testid={`ai-mapping-edit-detection-${detection.id}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={controller.busy}
                        sx={{ width: 28, height: 28 }}
                        onClick={(event) => {
                          event.stopPropagation();
                          controller.deleteDetection(areaId, detection.id);
                        }}
                        data-testid={`ai-mapping-delete-detection-${detection.id}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </MDBox>
                  ))}
                </MDBox>
              </Collapse>
            </MDBox>
          );
        })}
      </MDBox>
    </CollapsibleSection>
  );
}
